import React, { useState, useEffect } from 'react';
import apiClient from '../api/api'; // Adjust path as needed
import RechargeModal from '../components/Wallet/RechargeModal';
import { CreditCard, History, Wallet as WalletIcon } from 'lucide-react'; // Assuming lucide-react is installed as per package.json

const WalletPage = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [walletRes, historyRes] = await Promise.all([
        apiClient.get('/wallet'),
        apiClient.get('/wallet/history')
      ]);
      setWallet(walletRes.data);
      setTransactions(historyRes.data);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading Wallet...</div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
        <WalletIcon size={32} /> My Wallet
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Balance Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl shadow-xl p-8 text-white md:col-span-1 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
          
          <h2 className="text-xs font-black uppercase tracking-widest mb-6 opacity-60">My Balance</h2>
          
          <div className="mb-8">
            <p className="text-sm font-bold text-emerald-400 mb-1">Available</p>
            <div className="text-4xl font-black">
              {wallet ? `${wallet.currency_code} ${wallet.balance.toFixed(2)}` : '0.00'}
            </div>
          </div>

          <div className="mb-8 p-4 bg-white/5 rounded-2xl border border-white/10">
            <p className="text-xs font-bold text-orange-400 uppercase tracking-wider mb-1">Reserved (On Hold)</p>
            <div className="text-xl font-bold">
              {wallet ? `${wallet.currency_code} ${(wallet.reserved_balance || 0).toFixed(2)}` : '0.00'}
            </div>
          </div>

          <button 
            onClick={() => setIsRechargeModalOpen(true)}
            className="bg-emerald-600 text-white py-3 px-4 rounded-xl font-black hover:bg-emerald-700 transition-all flex items-center gap-2 w-full justify-center shadow-lg shadow-emerald-900/20 active:scale-95 mb-3"
          >
            <CreditCard size={18} /> Add Funds
          </button>

          <button 
            onClick={() => setIsWithdrawModalOpen(true)}
            className="bg-slate-700 text-white py-3 px-4 rounded-xl font-black hover:bg-slate-600 transition-all flex items-center gap-2 w-full justify-center shadow-lg active:scale-95"
          >
            <WalletIcon size={18} /> Withdraw
          </button>
        </div>

        {/* Info/Stats Card (Optional) */}
        <div className="bg-white rounded-xl shadow p-6 md:col-span-2 flex flex-col justify-center">
          <h3 className="text-xl font-semibold mb-2 text-gray-700">Wallet Info</h3>
          <p className="text-gray-600">
            Use your wallet balance to hire workers securely. Recharge instantly using your credit card via Stripe (in Test Mode).
            All transactions are secure and encrypted.
          </p>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <History size={24} /> Transaction History
            </h2>
            <button onClick={fetchData} className="text-blue-600 hover:text-blue-800 text-sm">Refresh</button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-600 uppercase text-sm font-semibold">
              <tr>
                <th className="p-4">Date</th>
                <th className="p-4">Description</th>
                <th className="p-4">Type</th>
                <th className="p-4 text-right">Amount</th>
                <th className="p-4">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.length > 0 ? (
                transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition">
                    <td className="p-4 text-gray-600 text-sm">
                      {new Date(tx.createdAt).toLocaleDateString()} {new Date(tx.createdAt).toLocaleTimeString()}
                    </td>
                    <td className="p-4 font-medium text-gray-800">{tx.description || '-'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        tx.type === 'CREDIT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className={`p-4 text-right font-bold ${
                         tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {tx.type === 'CREDIT' ? '+' : '-'}{tx.currency} {tx.amount.toFixed(2)}
                    </td>
                    <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            tx.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                            {tx.status}
                        </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-gray-500">
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <RechargeModal 
        isOpen={isRechargeModalOpen} 
        onClose={() => setIsRechargeModalOpen(false)} 
        onSuccess={fetchData} 
      />

      <WithdrawModal 
        isOpen={isWithdrawModalOpen} 
        onClose={() => setIsWithdrawModalOpen(false)} 
        onSuccess={fetchData} 
        balance={wallet?.balance || 0}
        currency={wallet?.currency_code || 'BDT'}
      />
    </div>
  );
};

const WithdrawModal = ({ isOpen, onClose, onSuccess, balance, currency }) => {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('Bkash');
    const [details, setDetails] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (parseFloat(amount) > balance) {
            alert("Insufficient balance.");
            return;
        }
        setLoading(true);
        try {
            await apiClient.post('/wallet/withdraw', { amount, method, details });
            alert("Withdrawal request submitted.");
            onSuccess();
            onClose();
        } catch (err) {
            alert("Failed to submit request.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white w-full max-w-md rounded-[2rem] p-8 shadow-2xl relative overflow-hidden">
                <h3 className="text-2xl font-black text-slate-800 mb-6">Withdraw Funds</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Amount ({currency})</label>
                        <input 
                            required type="number" step="0.01" 
                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-slate-800"
                            value={amount} onChange={e => setAmount(e.target.value)}
                            max={balance}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Available: {currency} {balance.toFixed(2)}</p>
                    </div>
                    <div>
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Method</label>
                        <select 
                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold"
                            value={method} onChange={e => setMethod(e.target.value)}
                        >
                            <option value="Bkash">Bkash</option>
                            <option value="Nagad">Nagad</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Account Details</label>
                        <input 
                            required type="text" 
                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-medium"
                            placeholder="e.g. Phone number or Bank Info"
                            value={details} onChange={e => setDetails(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button type="button" onClick={onClose} className="flex-1 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black">Cancel</button>
                        <button type="submit" disabled={loading} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black shadow-lg">
                            {loading ? 'Processing...' : 'Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default WalletPage;
